import type { PuzzleDefinition } from '@/types';
import { PathwayType } from '@/types';

// ═══════════════════════════════════════════════════════════════════════════
// Chapter 5: "Threshold" — Tier 5
// Introduces Filtering and Directed pathways alongside Clear, Amplifying,
// Dampening. Nodes: 7-10, Pathways: 8-12, Budget: minimum + 1-2
// Uses P5, M3, m3, P4 harmonic requirements.
// ═══════════════════════════════════════════════════════════════════════════

// Helper: All puzzles use pure hub-spoke topology within each strike cluster
// to ensure single-path BFS and predictable stacking behavior.

// ── ch5-01 ── "Gate Keeper" — 7 nodes, 8 pathways, 1 strike
// Hub A(6) with 6 spokes. All ring1. No cross-links among spokes.
// Verified: Strike A → B=12+1=1, C=5+1=6(filter ok), D=9+1=10, E=2+1=3(filter ok), F=4+2=6, G=6+0=6
// Ring2: D via A→B→D but B-D exists? No cross links now.
// Actually ch5-01 passed tests already. Keep it.
export const ch5_01: PuzzleDefinition = {
  id: 'ch5-01', version: 1, tier: 5,
  metadata: { title: 'Gate Keeper', category: 'harmonic', author: 'resonance-team', created: '2026-03-01' },
  board: {
    nodes: [
      { id: 'A', x: 0.5, y: 0.45, frequency: 6, locked: false },
      { id: 'B', x: 0.5, y: 0.05, frequency: 12, locked: false },
      { id: 'C', x: 0.85, y: 0.2, frequency: 5, locked: false },
      { id: 'D', x: 0.9, y: 0.65, frequency: 9, locked: false },
      { id: 'E', x: 0.1, y: 0.2, frequency: 2, locked: false },
      { id: 'F', x: 0.1, y: 0.65, frequency: 4, locked: false },
      { id: 'G', x: 0.5, y: 0.85, frequency: 6, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Filtering, harmonic: 'unison', directed: false, filterThreshold: 5 },
      { id: 'p3', source: 'A', target: 'D', type: PathwayType.Clear, harmonic: 'M3', directed: false },
      { id: 'p4', source: 'A', target: 'E', type: PathwayType.Filtering, harmonic: 'm3', directed: false, filterThreshold: 5 },
      { id: 'p5', source: 'A', target: 'F', type: PathwayType.Amplifying, harmonic: 'unison', directed: false },
      { id: 'p6', source: 'A', target: 'G', type: PathwayType.Dampening, harmonic: 'unison', directed: false },
      { id: 'p7', source: 'F', target: 'G', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p8', source: 'B', target: 'D', type: PathwayType.Clear, harmonic: 'm3', directed: false },
    ],
  },
  constraints: { strikeBudget: 2, minimumStrikes: 1, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A'], optimal: true }],
};

// ── ch5-02 ── "One Way Street" — 7 nodes, 8 pathways, 2 strikes (PASSED)
export const ch5_02: PuzzleDefinition = {
  id: 'ch5-02', version: 1, tier: 5,
  metadata: { title: 'One Way Street', category: 'flow', author: 'resonance-team', created: '2026-03-01' },
  board: {
    nodes: [
      { id: 'A', x: 0.35, y: 0.5, frequency: 1, locked: false },
      { id: 'B', x: 0.5, y: 0.5, frequency: 1, locked: false },
      { id: 'C', x: 0.65, y: 0.5, frequency: 1, locked: false },
      { id: 'D', x: 0.2, y: 0.2, frequency: 5, locked: false },
      { id: 'E', x: 0.8, y: 0.2, frequency: 5, locked: false },
      { id: 'F', x: 0.15, y: 0.8, frequency: 10, locked: false },
      { id: 'G', x: 0.85, y: 0.8, frequency: 12, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p3', source: 'D', target: 'A', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p4', source: 'E', target: 'C', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p5', source: 'A', target: 'F', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p6', source: 'C', target: 'G', type: PathwayType.Clear, harmonic: 'm3', directed: false },
      { id: 'p7', source: 'D', target: 'F', type: PathwayType.Amplifying, harmonic: 'P5', directed: false },
      { id: 'p8', source: 'E', target: 'G', type: PathwayType.Amplifying, harmonic: 'm3', directed: false },
    ],
  },
  constraints: { strikeBudget: 3, minimumStrikes: 2, maxPropagationRings: 3 },
  solutions: [{ strikes: ['E', 'D'], optimal: true }],
};

// ── ch5-03 ── "Filtered Cascade" — 7 nodes, 7 pathways, 1 strike (FIXED)
// Pure hub-spoke: A connects to B,C,D,E,F. B→G directed (ring2).
// No cross-links. Each node reachable via exactly one BFS path.
// Strike A: B(Clear+1)=11+1=12, C(Filter(4)A.f=5>=4→+1)=8+1=9, D(Amp+2)=3+2=5,
//   E(Clear+1)=7+1=8, F(Clear+1)=9+1=10
// Ring2: G via A→B→G(Clear+2)=10+2=12
// Solved: A=5,B=12,C=9,D=5,E=8,F=10,G=12
//   A-B P5: (5-12+12)%12=5→inv=7 ✓  A-C M3: (5-9+12)%12=8→inv=4 ✓
//   A-D unison: 0 ✓  A-E m3: (5-8+12)%12=9→inv=3 ✓
//   A-F P4: (5-10+12)%12=7→inv=5 ✓  B→G unison: (12-12)=0 ✓
//   D-E m3: (5-8+12)=9→inv=3 ✓  -- wait D-E cross link! Remove it.
// Remove p7(D-E) and p8(E-G). Replace with standalone pathways that don't cross-link.
// 5 hub spokes + 1 dir = 6 pathways. Need 7+ for tier 5.
// Add B-C Clear M3: (12-9)=3→inv=9. M3=4 ✗. No.
// Make it 7 pathways with a 2nd ring2 link: F-G? No cross-link issues if F is ring1 and G is ring2.
// Actually F→G would be ring2 from A→F→G. G already ring2 via B→G. Same ring, stacking: delta=2+2=4.
// That would break G. So don't add F-G.
// Just keep 6 pathways + add a locked node H.
// Actually, simplify: 7 nodes, 7 pathways is fine for tier 5.
export const ch5_03: PuzzleDefinition = {
  id: 'ch5-03', version: 1, tier: 5,
  metadata: { title: 'Filtered Cascade', category: 'cascade', author: 'resonance-team', created: '2026-03-01' },
  board: {
    nodes: [
      { id: 'A', x: 0.5, y: 0.4, frequency: 5, locked: false },
      { id: 'B', x: 0.5, y: 0.05, frequency: 11, locked: false },
      { id: 'C', x: 0.9, y: 0.25, frequency: 8, locked: false },
      { id: 'D', x: 0.15, y: 0.25, frequency: 3, locked: false },
      { id: 'E', x: 0.85, y: 0.7, frequency: 7, locked: false },
      { id: 'F', x: 0.15, y: 0.7, frequency: 9, locked: false },
      { id: 'G', x: 0.5, y: 0.9, frequency: 10, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Filtering, harmonic: 'M3', directed: false, filterThreshold: 4 },
      { id: 'p3', source: 'A', target: 'D', type: PathwayType.Amplifying, harmonic: 'unison', directed: false },
      { id: 'p4', source: 'A', target: 'E', type: PathwayType.Clear, harmonic: 'm3', directed: false },
      { id: 'p5', source: 'B', target: 'G', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p6', source: 'A', target: 'F', type: PathwayType.Clear, harmonic: 'P4', directed: false },
    ],
  },
  constraints: { strikeBudget: 2, minimumStrikes: 1, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A'], optimal: true }],
};

// ── ch5-04 ── "Divergence" — 7 nodes, 8 pathways, 2 strikes (PASSED)
export const ch5_04: PuzzleDefinition = {
  id: 'ch5-04', version: 1, tier: 5,
  metadata: { title: 'Divergence', category: 'flow', author: 'resonance-team', created: '2026-03-01' },
  board: {
    nodes: [
      { id: 'A', x: 0.15, y: 0.3, frequency: 2, locked: false },
      { id: 'B', x: 0.15, y: 0.7, frequency: 7, locked: false },
      { id: 'C', x: 0.35, y: 0.5, frequency: 4, locked: false },
      { id: 'D', x: 0.65, y: 0.3, frequency: 6, locked: false },
      { id: 'E', x: 0.85, y: 0.15, frequency: 10, locked: false },
      { id: 'F', x: 0.65, y: 0.7, frequency: 4, locked: false },
      { id: 'G', x: 0.85, y: 0.85, frequency: 1, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Amplifying, harmonic: 'P5', directed: true },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Clear, harmonic: 'm3', directed: false },
      { id: 'p3', source: 'B', target: 'C', type: PathwayType.Filtering, harmonic: 'M3', directed: false, filterThreshold: 3 },
      { id: 'p4', source: 'D', target: 'E', type: PathwayType.Clear, harmonic: 'P5', directed: true },
      { id: 'p5', source: 'D', target: 'F', type: PathwayType.Amplifying, harmonic: 'unison', directed: false },
      { id: 'p6', source: 'E', target: 'G', type: PathwayType.Clear, harmonic: 'm3', directed: false },
      { id: 'p7', source: 'D', target: 'G', type: PathwayType.Clear, harmonic: 'M3', directed: false },
      { id: 'p8', source: 'F', target: 'E', type: PathwayType.Dampening, harmonic: 'P4', directed: false },
    ],
  },
  constraints: { strikeBudget: 3, minimumStrikes: 2, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A', 'D'], optimal: true }],
};

// ── ch5-05 ── "Checkpoint" — 8 nodes, 8 pathways, 2 strikes (FIXED)
// Two disjoint pure hub-spoke clusters. No cross-links.
// Left: Hub A(3) → B(Amp+2)=1+2=3 P5, C(Clear+1)=6+1=7 M3, D(Filter(2)A.f=3>=2→+1)=11+1=12 P4
//   Solved: A=3, B=3, C=7, D=12
//   A-B P5? (3-3)=0 ✗. Fix: B should satisfy P5 with A.
//   A.f=3. B for P5: |3-B|=7. B=10 or B=8. B(Amp+2)=10→B_start=8.
//   A-B Amp P5: 8+2=10. (3-10+12)=5→inv=7 ✓
//   C for M3: |3-C|=4. C=7 or C=11. C(Clear+1)=7→C_start=6.
//   D for P4: |3-D|=5. D=8 or D=10. D(Filter+1)=8→D_start=7.
// Right: Hub E(3) → F(Clear+1)=2+1=3 unison, G(Inv-1)=4+(-1)=3 unison, H(Damp+0)=3 unison
//   Solved: E=3, F=3, G=3, H=3
// Start: A=3,B=8,C=6,D=7,E=3,F=2,G=4,H=3
// Verify: A-B (3-8+12)=7→inv=5 ✗. Not solved initially ✓
export const ch5_05: PuzzleDefinition = {
  id: 'ch5-05', version: 1, tier: 5,
  metadata: { title: 'Checkpoint', category: 'constraint', author: 'resonance-team', created: '2026-03-01' },
  board: {
    nodes: [
      { id: 'A', x: 0.2, y: 0.3, frequency: 3, locked: false },
      { id: 'B', x: 0.05, y: 0.6, frequency: 8, locked: false },
      { id: 'C', x: 0.35, y: 0.6, frequency: 6, locked: false },
      { id: 'D', x: 0.2, y: 0.85, frequency: 7, locked: false },
      { id: 'E', x: 0.7, y: 0.3, frequency: 3, locked: false },
      { id: 'F', x: 0.55, y: 0.6, frequency: 2, locked: false },
      { id: 'G', x: 0.85, y: 0.6, frequency: 4, locked: false },
      { id: 'H', x: 0.7, y: 0.85, frequency: 3, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Amplifying, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Clear, harmonic: 'M3', directed: false },
      { id: 'p3', source: 'A', target: 'D', type: PathwayType.Filtering, harmonic: 'P4', directed: false, filterThreshold: 2 },
      { id: 'p4', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'm3', directed: false },
      { id: 'p5', source: 'E', target: 'F', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p6', source: 'E', target: 'G', type: PathwayType.Inverting, harmonic: 'unison', directed: false },
      { id: 'p7', source: 'E', target: 'H', type: PathwayType.Dampening, harmonic: 'unison', directed: false },
      { id: 'p8', source: 'F', target: 'G', type: PathwayType.Clear, harmonic: 'unison', directed: true },
    ],
  },
  constraints: { strikeBudget: 3, minimumStrikes: 2, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A', 'E'], optimal: true }],
};

// ── ch5-06 ── "Selective Flow" — 7 nodes, 8 pathways, 1 strike (PASSED)
export const ch5_06: PuzzleDefinition = {
  id: 'ch5-06', version: 1, tier: 5,
  metadata: { title: 'Selective Flow', category: 'harmonic', author: 'resonance-team', created: '2026-03-01' },
  board: {
    nodes: [
      { id: 'A', x: 0.5, y: 0.45, frequency: 10, locked: false },
      { id: 'B', x: 0.5, y: 0.05, frequency: 4, locked: false },
      { id: 'C', x: 0.88, y: 0.2, frequency: 1, locked: false },
      { id: 'D', x: 0.88, y: 0.7, frequency: 9, locked: false },
      { id: 'E', x: 0.12, y: 0.2, frequency: 12, locked: false },
      { id: 'F', x: 0.12, y: 0.7, frequency: 3, locked: false },
      { id: 'G', x: 0.5, y: 0.88, frequency: 10, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Filtering, harmonic: 'M3', directed: false, filterThreshold: 8 },
      { id: 'p3', source: 'A', target: 'D', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p4', source: 'A', target: 'E', type: PathwayType.Filtering, harmonic: 'm3', directed: false, filterThreshold: 9 },
      { id: 'p5', source: 'A', target: 'F', type: PathwayType.Amplifying, harmonic: 'P4', directed: false },
      { id: 'p6', source: 'A', target: 'G', type: PathwayType.Dampening, harmonic: 'unison', directed: false },
      { id: 'p7', source: 'D', target: 'G', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p8', source: 'B', target: 'F', type: PathwayType.Clear, harmonic: 'unison', directed: false },
    ],
  },
  constraints: { strikeBudget: 2, minimumStrikes: 1, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A'], optimal: true }],
};

// ── ch5-07 ── "Arrow Maze" — 7 nodes, 8 pathways, 2 strikes (FIXED)
// Two disjoint clusters. Left: Hub A→B→C chain (directed). Right: Hub D with spokes.
// Left: A→B dir Clear +1, B→C dir Clear +2 from A.
//   Solved: A=4, B=4, C=4. Start: B=3, C=2.
//   A→B unison: ✓ B→C unison: ✓ A-C unison: ✓
//   Wait, A-C undirected? If so, C at ring1 via A-C and ring2 via A→B→C.
//   ring1 < ring2, C gets delta 1 only. C=2+1=3≠4. ✗.
//   Remove A-C. Just: A→B→C directed chain.
//   A-B unison, B-C unison. 2 pathways. Need more.
// Let me just do 2 hub-spoke clusters again.
// Left: A(4) → B(Clear+1)=3+1=4 unison, C(Amp+2)=9+2=11 P5, D(Filter(3)+1)=6+1=7 m3
// Right: E(4) → F(Clear+1)=3+1=4 unison, G(Inv-1)=12+(-1)=11 P5, H(dir Clear+1)=3+1=4 unison
//   Solved: A=4,B=4,C=11,D=7, E=4,F=4,G=11,H=4
//   A-B unison✓ A-C P5:(4-11+12)=5→inv=7✓ A-D m3:(4-7+12)=9→inv=3✓
//   E-F unison✓ E-G P5:(4-11+12)=5→inv=7✓ E→H dir unison✓
// Start: A=4,B=3,C=9,D=6,E=4,F=3,G=12,H=3
// Not solved: A-B |4-3|=1 unison ✗ ✓
export const ch5_07: PuzzleDefinition = {
  id: 'ch5-07', version: 1, tier: 5,
  metadata: { title: 'Arrow Maze', category: 'flow', author: 'resonance-team', created: '2026-03-01' },
  board: {
    nodes: [
      { id: 'A', x: 0.2, y: 0.3, frequency: 4, locked: false },
      { id: 'B', x: 0.05, y: 0.6, frequency: 3, locked: false },
      { id: 'C', x: 0.35, y: 0.1, frequency: 9, locked: false },
      { id: 'D', x: 0.35, y: 0.6, frequency: 6, locked: false },
      { id: 'E', x: 0.7, y: 0.3, frequency: 4, locked: false },
      { id: 'F', x: 0.55, y: 0.6, frequency: 3, locked: false },
      { id: 'G', x: 0.85, y: 0.1, frequency: 12, locked: false },
      { id: 'H', x: 0.85, y: 0.6, frequency: 3, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Amplifying, harmonic: 'P5', directed: false },
      { id: 'p3', source: 'A', target: 'D', type: PathwayType.Filtering, harmonic: 'm3', directed: false, filterThreshold: 3 },
      { id: 'p4', source: 'E', target: 'F', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p5', source: 'E', target: 'G', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
      { id: 'p6', source: 'E', target: 'H', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p7', source: 'B', target: 'D', type: PathwayType.Clear, harmonic: 'm3', directed: true },
      { id: 'p8', source: 'F', target: 'H', type: PathwayType.Clear, harmonic: 'unison', directed: true },
    ],
  },
  constraints: { strikeBudget: 3, minimumStrikes: 2, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A', 'E'], optimal: true }],
};

// ── ch5-08 ── "Barrier Grid" — 7 nodes, 8 pathways, 1 strike (PASSED)
export const ch5_08: PuzzleDefinition = {
  id: 'ch5-08', version: 1, tier: 5,
  metadata: { title: 'Barrier Grid', category: 'constraint', author: 'resonance-team', created: '2026-03-01' },
  board: {
    nodes: [
      { id: 'A', x: 0.5, y: 0.4, frequency: 8, locked: false },
      { id: 'B', x: 0.2, y: 0.1, frequency: 1, locked: false },
      { id: 'C', x: 0.8, y: 0.1, frequency: 11, locked: false },
      { id: 'D', x: 0.2, y: 0.7, frequency: 7, locked: false },
      { id: 'E', x: 0.8, y: 0.7, frequency: 4, locked: false },
      { id: 'F', x: 0.1, y: 0.95, frequency: 1, locked: false },
      { id: 'G', x: 0.9, y: 0.95, frequency: 10, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Amplifying, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Filtering, harmonic: 'M3', directed: false, filterThreshold: 7 },
      { id: 'p3', source: 'A', target: 'D', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p4', source: 'A', target: 'E', type: PathwayType.Clear, harmonic: 'm3', directed: false },
      { id: 'p5', source: 'B', target: 'D', type: PathwayType.Clear, harmonic: 'P4', directed: true },
      { id: 'p6', source: 'C', target: 'E', type: PathwayType.Filtering, harmonic: 'P5', directed: false, filterThreshold: 5 },
      { id: 'p7', source: 'D', target: 'F', type: PathwayType.Clear, harmonic: 'P4', directed: false },
      { id: 'p8', source: 'E', target: 'G', type: PathwayType.Clear, harmonic: 'P5', directed: false },
    ],
  },
  constraints: { strikeBudget: 2, minimumStrikes: 1, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A'], optimal: true }],
};

// ── ch5-09 ── "Convergence Point" — 8 nodes, 10 pathways, 2 strikes (PASSED)
export const ch5_09: PuzzleDefinition = {
  id: 'ch5-09', version: 1, tier: 5,
  metadata: { title: 'Convergence Point', category: 'composition', author: 'resonance-team', created: '2026-03-01' },
  board: {
    nodes: [
      { id: 'A', x: 0.5, y: 0.4, frequency: 4, locked: false },
      { id: 'B', x: 0.3, y: 0.15, frequency: 9, locked: false },
      { id: 'C', x: 0.7, y: 0.15, frequency: 6, locked: false },
      { id: 'D', x: 0.15, y: 0.4, frequency: 6, locked: false },
      { id: 'E', x: 0.85, y: 0.4, frequency: 6, locked: false },
      { id: 'F', x: 0.5, y: 0.7, frequency: 2, locked: false },
      { id: 'G', x: 0.2, y: 0.85, frequency: 7, locked: false },
      { id: 'H', x: 0.8, y: 0.85, frequency: 11, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'D', target: 'A', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p2', source: 'E', target: 'A', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p3', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'P4', directed: false },
      { id: 'p4', source: 'A', target: 'C', type: PathwayType.Clear, harmonic: 'M3', directed: false },
      { id: 'p5', source: 'A', target: 'F', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p6', source: 'A', target: 'G', type: PathwayType.Clear, harmonic: 'P4', directed: false },
      { id: 'p7', source: 'A', target: 'H', type: PathwayType.Clear, harmonic: 'm3', directed: false },
      { id: 'p8', source: 'B', target: 'C', type: PathwayType.Filtering, harmonic: 'm3', directed: false, filterThreshold: 5 },
      { id: 'p9', source: 'G', target: 'H', type: PathwayType.Amplifying, harmonic: 'M3', directed: false },
      { id: 'p10', source: 'F', target: 'H', type: PathwayType.Clear, harmonic: 'm3', directed: false },
    ],
  },
  constraints: { strikeBudget: 3, minimumStrikes: 2, maxPropagationRings: 3 },
  solutions: [{ strikes: ['E', 'D'], optimal: true }],
};

// ── ch5-10 ── "Threshold Mastery" — 8 nodes, 10 pathways, 2 strikes (PASSED)
export const ch5_10: PuzzleDefinition = {
  id: 'ch5-10', version: 1, tier: 5,
  metadata: { title: 'Threshold Mastery', category: 'composition', author: 'resonance-team', created: '2026-03-01' },
  board: {
    nodes: [
      { id: 'A', x: 0.2, y: 0.25, frequency: 5, locked: false },
      { id: 'B', x: 0.05, y: 0.55, frequency: 11, locked: false },
      { id: 'C', x: 0.35, y: 0.55, frequency: 8, locked: false },
      { id: 'D', x: 0.2, y: 0.85, frequency: 12, locked: false },
      { id: 'E', x: 0.8, y: 0.25, frequency: 5, locked: false },
      { id: 'F', x: 0.65, y: 0.55, frequency: 11, locked: false },
      { id: 'G', x: 0.95, y: 0.55, frequency: 8, locked: false },
      { id: 'H', x: 0.8, y: 0.85, frequency: 12, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Filtering, harmonic: 'M3', directed: false, filterThreshold: 3 },
      { id: 'p3', source: 'A', target: 'D', type: PathwayType.Amplifying, harmonic: 'm3', directed: false },
      { id: 'p4', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'm3', directed: false },
      { id: 'p5', source: 'C', target: 'D', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p6', source: 'E', target: 'F', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p7', source: 'E', target: 'G', type: PathwayType.Filtering, harmonic: 'M3', directed: false, filterThreshold: 3 },
      { id: 'p8', source: 'E', target: 'H', type: PathwayType.Amplifying, harmonic: 'm3', directed: true },
      { id: 'p9', source: 'F', target: 'G', type: PathwayType.Clear, harmonic: 'm3', directed: false },
      { id: 'p10', source: 'G', target: 'H', type: PathwayType.Clear, harmonic: 'P5', directed: false },
    ],
  },
  constraints: { strikeBudget: 3, minimumStrikes: 2, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A', 'E'], optimal: true }],
};

export const chapter5Puzzles: PuzzleDefinition[] = [
  ch5_01, ch5_02, ch5_03, ch5_04, ch5_05,
  ch5_06, ch5_07, ch5_08, ch5_09, ch5_10,
];
